import React, { useEffect, useState } from 'react'
import { Controller, useFieldArray, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Accordion, Button, Form, Icon, Popup, Transition } from 'semantic-ui-react'
import FTPService from '~/api/services/ftp'
import { FormFields } from '~/components/FormFields'
import { emailRegex } from '~/components/FormFields/constants'
import InfoPopup from '~/components/InfoPopup'
import notify from '~/components/Notify'
import { StyledAccordion, StyledLoader, StyledTitle } from './style'
import { timeRegex } from '~/Constants'
import CustomIcon from '~/components/CustomIcon'
import { TimeInput } from 'semantic-ui-calendar-react'
interface IFTPCardProps {
  ftp: any
  index: number
  handleRecipientsHeaderClick(e: any, titleProps: any): void
  active: boolean
  control: any
  options: any
  getValues: any
  setValue: any
  watchScheduledActiveField: boolean
}

const FTPCard: React.FC<IFTPCardProps> = (props) => {
  const { t } = useTranslation()
  const [loadingConnection, setLoadingConnection] = useState(false)
  const [connection, setConnection] = useState(false)
  const {
    ftp,
    index,
    handleRecipientsHeaderClick,
    active,
    control,
    options,
    getValues,
    setValue,
    watchScheduledActiveField
  } = props

  const notificationEmails = useFieldArray({
    control,
    name: `ftps[${index}].notificationEmails`
  });

  const watchNotificatonStatus = useWatch({ control, name: `ftps[${index}].notificationStatus` })

  useEffect(() => {
    const testedConnection = getValues(`ftps[${index}].connection`)
    if (ftp && testedConnection === undefined) {
      checkConnection()
    }
  }, [ftp, active])

  useEffect(() => {
    if (watchNotificatonStatus && notificationEmails.fields.length === 0) {
      notificationEmails.append({ email: '' })
    }
  }, [watchNotificatonStatus])

  async function checkConnection() {
    const {
      protocol,
      ftpIp,
      ftpPort,
      ftpUser,
      ftpPassword
    } = ftp

    setLoadingConnection(true)
    try {
      const response = await FTPService.checkFtpConnection({
        protocol,
        ftpIp,
        ftpPort,
        ftpUser,
        ftpPassword
      })
      setValue(`ftps[${index}].connection`, Boolean(response.data))
      setConnection(Boolean(response.data))
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadingConnection(false)
  }

  function renderGroups(groupNumber, index) {
    return Array.from({ length: groupNumber }, (_, i) => {
      const groupName = `ftps[${index}].group${i + 1}`
      const groupValue = getValues(groupName)
      return (
        <FormFields.Select
          key={groupName}
          control={control}
          options={options?.farmerGroups.map(farmerGroup => ({ value: farmerGroup.id, text: farmerGroup.name }))}
          selection
          placeholder={t('Select group')}
          name={groupName}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please select group')
            }
          } : null}
          label={t('Farmer group ({{i}})', { i: i + 1 })}
          selectOnBlur={false}
        />
      )
    });
  }

  function renderFtpContent(ftp, index) {
    const ftpsName = `ftps[${index}]`
    const groupNumber = ftp.groupsNumber
    return <>
      <Form.Group style={{ alignItems: 'center' }} widths='equal'>
        <FormFields.Checkbox
          name={`${ftpsName}.schedulerActive`}
          control={control}
          defaultValue={0}
          style={{ marginTop: '1em' }}
          checkboxValue={1}
          label={ftp.type === 'export' ? t('Scheduled exporting') : t('Scheduled importing')}
        />
        <FormFields.Select
          control={control}
          options={options?.days.map(option => ({ value: option.key.toString(), text: option.label }))}
          selection
          placeholder={t('Select days')}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please select days')
            }
          } : null}
          name={`${ftpsName}.schedulerDays`}
          multiple
          label={<div style={{ display: 'flex', marginBottom: '0.5rem', fontWeight: '700', color: '#5e6672' }}>
            <label>{ftp?.type === 'export' ? t('Export days') : t('Importing days')}</label>
            <InfoPopup
              content={t('On which days should data be imported/exported? (Multiple selection possible)')}
              styleIcon={{ marginLeft: 3, cursor: 'pointer' }}
            />
          </div>}
          selectOnBlur={false}
        />
        <Controller
          rules={{
            required: {
              value: true,
              message: t('Please enter time')
            },
            pattern: {
              value: timeRegex,
              message: t('Please enter time in format HH:mm')
            }
          }}
          control={control}
          name={`${ftpsName}.schedulerTime`}
          render={({ field: { value } }) => {
            return (
              <Form.Field>
                <label style={{ display: 'flex', marginBottom: '0.5rem', fontWeight: '700', color: '#5e6672' }}>
                  <div>{ftp.type === 'export' ? t('Export time') : t('Importing time')}</div>
                  <InfoPopup
                    content={t('At what time should the data be imported/exported? (If you have selected multiple days, the selected time applies to all days).')}
                    styleIcon={{ marginLeft: 3, cursor: 'pointer' }}
                  />
                </label>
                <TimeInput
                  name='time'
                  placeholder={ftp?.type === 'export' ? t('Export time') : t('Importing time')}
                  value={value}
                  iconPosition='left'
                  onChange={(_, { value }) => setValue(`${ftpsName}.schedulerTime`, value === '' ? null : value)}
                  popupPosition='bottom left'
                  closable
                />
              </Form.Field>
            )
          }}
        />
      </Form.Group>
      <Form.Group style={{ alignItems: 'center' }} widths='equal'>
        <FormFields.Select
          control={control}
          options={options?.protocols.map((protocol, index) => ({ value: index, text: protocol }))}
          selection
          placeholder={t('Select FTPS')}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please select protocol')
            }
          } : null}
          name={`${ftpsName}.protocol`}
          label={t('FTPS protocol')}
          selectOnBlur={false}
        />
        <FormFields.Input
          control={control}
          name={`${ftpsName}.ftpIp`}
          label={t('IP address')}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please enter ip adress')
            }
          } : null}
          placeholder={t('Enter ip adress')}
        />
        <FormFields.Input
          control={control}
          name={`${ftpsName}.ftpPort`}
          label={t('Port')}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please enter port')
            }
          } : null}
          placeholder={t('Enter port')}
        />
      </Form.Group>
      <Form.Group style={{ alignItems: 'center' }} widths='equal'>
        <FormFields.Input
          control={control}
          name={`${ftpsName}.ftpUser`}
          label={t('User')}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please enter user')
            }
          } : undefined}
          placeholder={t('Enter user')}
        />
        <FormFields.Input
          control={control}
          type={'password'}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please enter password')
            }
          } : null}
          name={`${ftpsName}.ftpPassword`}
          label={t('Password')}
          placeholder={t('Enter password')}
        />
      </Form.Group>
      <Form.Group style={{ alignItems: 'center' }} widths='equal'>
        {parseInt(groupNumber) > 0 && renderGroups(groupNumber, index)}
      </Form.Group>
      <Form.Group style={{ alignItems: 'center' }} widths='equal'>
        <FormFields.Input
          control={control}
          name={`${ftpsName}.folder`}
          label={ftp?.type === 'export' ? t('Farmers export folder') : t('Farmers import folder')}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please enter folder name')
            }
          } : null}
          placeholder={ftp?.type === 'export' ? t('Enter export folder') : t('Enter import folder')}
        />
        {ftp.type === 'export' && <FormFields.Select
          control={control}
          options={options?.formats.map((format, index) => ({ value: format.key, text: format.label }))}
          selection
          placeholder={t('Select format')}
          rules={watchScheduledActiveField ? {
            required: {
              value: true,
              message: t('Please select format')
            }
          } : null}
          name={`${ftpsName}.exportFormat`}
          label={t('FTPS format')}
          selectOnBlur={false}
        />
        }
      </Form.Group>
      <FormFields.Checkbox
        control={control}
        name={`${ftpsName}.notificationStatus`}
        label={t('Notification status')}
        defaultValue={0}
        checkboxValue={1}
      />
      <Form.Field
        required={Boolean(watchNotificatonStatus)}
        style={{ display: 'flex', alignItems: 'baseline' }}
      >
        <label style={{ marginRight: 10 }}>{t('Notification emails')}</label>
        <Popup
          content={t('Add new notification email')}
          trigger={
            <Button
              onClick={() => notificationEmails.append({ email: '' })}
              primary
              size='mini'
              type={'button'}
              icon='plus'
            />
          }
        />
      </Form.Field>
      <>
        {notificationEmails.fields.map((notificationEmail, notificationEmailIndex) => {
          return <Form.Group key={notificationEmail.id}>
            <FormFields.Input
              control={control}
              width={12}
              placeholder={t('Email')}
              label={t('New email')}
              name={`${ftpsName}.notificationEmails[${notificationEmailIndex}].email`}
              rules={{
                required: {
                  value: true,
                  message: t('Please enter email')
                },
                pattern: {
                  value: emailRegex,
                  message: t('Please enter valid email')
                }
              }}
            />
            <Form.Button
              style={{ marginLeft: 10, marginTop: 24 }}
              onClick={() => notificationEmails.remove(notificationEmailIndex)}
              size={'small'}
              basic
              color='red'
              type={'button'}
              icon='trash'
            />
          </Form.Group>
        })}
      </>
    </>
  }

  function renderRefreshConnection() {
    return <Popup
      content={t('Check connection')}
      trigger={<CustomIcon
        name='sync'
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          checkConnection()
        }} />}
    />
  }

  return <StyledAccordion styled key={ftp.id}>
    <Accordion.Title
      index={index}
      active={active}
      onClick={handleRecipientsHeaderClick}
    >
      <StyledTitle color={loadingConnection ? 'black' : connection ? 'green' : 'red'}>
        {!loadingConnection ? renderRefreshConnection() : <StyledLoader active={loadingConnection} />}
        {ftp.label}
      </StyledTitle>
      <Icon name={active ? 'minus' : 'plus'} />
    </Accordion.Title>
    <Transition visible={active} animation='slide down' duration={200}>
      <Accordion.Content
        active={active}
        content={renderFtpContent(ftp, index)}
      />
    </Transition>
  </StyledAccordion>
}
export default FTPCard